import { memo } from 'react';
import type { GameRuntime } from '../../game/runtime/GameRuntime';
import type { RuntimeUISnapshot } from '../../game/runtime/RuntimeUIStore';
import { formatScore } from './Hud';
import { BootLockup } from './TitleIntro';

interface RuntimeProps { runtime: GameRuntime; snapshot: RuntimeUISnapshot; }

/** Sheet 01 verbatim; the intro overlay paints the same lockup on top of it,
 *  so a slow runtime boot reads as one continuous frame rather than a swap.
 *  Fix 3(b): memoized — all panels in this file only need re-rendering when
 *  their own props change, not on every unrelated App re-render. */
export const BootPanel = memo(function BootPanel() {
  return (
    <section className="boot-panel" aria-live="polite">
      <BootLockup />
    </section>
  );
});

export const ControlsPanel = memo(function ControlsPanel({ runtime }: { runtime: GameRuntime }) {
  return (
    <section className="modal-panel controls-panel" aria-labelledby="controls-title">
      <p className="eyebrow">VEIL SKIMMER INPUT MAP</p>
      <h2 id="controls-title">CONTROLS</h2>
      <div className="control-columns">
        <ControlGroup title="KEYBOARD" rows={[
          ['MOVE', 'WASD / ARROWS'], ['FIRE', 'J / SPACE'], ['AIM LOCK', 'K'], ['FORMATION', 'L'],
          ['NODE DIRECTION', 'I / O'], ['DEVICE', 'U'], ['PAUSE', 'ESC'],
        ]} />
        <ControlGroup title="GAMEPAD" rows={[
          ['MOVE', 'LEFT STICK / D-PAD'], ['FIRE', 'A / RT'], ['AIM LOCK', 'LB'], ['FORMATION', 'B'],
          ['NODE DIRECTION', 'RB'], ['DEVICE', 'X'], ['PAUSE', 'MENU'],
        ]} />
        <ControlGroup title="TOUCH" rows={[
          ['MOVE', 'DRAG ANYWHERE'], ['FIRE', 'HOLD / AUTO FIRE'], ['TACTICS', 'FORM / DIR / DEVICE'],
          ['PORTRAIT', 'GAME VIEW + TOUCH DECK'],
        ]} />
      </div>
      <p className="panel-note">PRISM NODES intercept eligible bullets. Lasers, piercing rounds and blast fields pass through.</p>
      <div className="panel-actions">
        <button data-menu-item type="button" className="button primary" onClick={() => runtime.returnToTitle()}>RETURN</button>
      </div>
    </section>
  );
});

interface PausePanelProps extends RuntimeProps { onOpenSettings: () => void; }

export const PausePanel = memo(function PausePanel({ runtime, snapshot, onOpenSettings }: PausePanelProps) {
  return (
    <section className="modal-panel pause-panel" aria-labelledby="pause-title">
      <p className="eyebrow">SIMULATION CLOCK HALTED</p>
      <h2 id="pause-title">PAUSED</h2>
      <dl className="pause-loadout">
        <div><dt>CORE</dt><dd>{snapshot.coreGlyph} {snapshot.coreName}</dd></div>
        <div><dt>LENS</dt><dd>{snapshot.lensGlyph} {snapshot.lensName}</dd></div>
        <div><dt>DEVICE</dt><dd>{snapshot.deviceGlyph} {snapshot.deviceName}</dd></div>
        <div><dt>FORMATION</dt><dd>{snapshot.formation}</dd></div>
      </dl>
      <div className="panel-actions vertical">
        <button data-menu-item type="button" className="button primary" onClick={() => runtime.resume()}>RESUME</button>
        <button data-menu-item type="button" className="button" onClick={onOpenSettings}>SETTINGS</button>
        <button data-menu-item type="button" className="button danger" onClick={() => runtime.returnToTitle()}>ABANDON RUN</button>
      </div>
    </section>
  );
});

export const StageResultPanel = memo(function StageResultPanel({ runtime, snapshot }: RuntimeProps) {
  const ranking = snapshot.stageRanking;
  if (ranking) {
    const placement = ranking.placement > 0 ? `LOCAL #${ranking.placement}` : 'OUTSIDE LOCAL TOP 5';
    return (
      <section
        className={`modal-panel result-panel result-panel-ranked rank-${ranking.grade.toLowerCase()}`}
        aria-labelledby="result-title"
        aria-live="polite"
      >
        <header className="result-heading">
          <div>
            <p className="eyebrow">ROUTE LAYER STABILIZED</p>
            <h2 id="result-title">STAGE {snapshot.stageId + 1} CLEAR</h2>
          </div>
          <span>{snapshot.difficultyName}</span>
        </header>

        <div className="result-main">
          <div className="result-stage-score">
            <span>STAGE SCORE</span>
            <strong>{formatScore(ranking.stageScore)}</strong>
            <small>{ranking.isNewBest ? 'NEW PERSONAL RECORD' : placement}</small>
          </div>
          <div className="result-rank" aria-label={`Stage rank ${ranking.grade}`}>
            <span>RANK</span>
            <strong>{ranking.grade}</strong>
            <small>{placement}</small>
          </div>
        </div>

        <div className="result-total">
          <span>RUN TOTAL</span>
          <strong>{formatScore(snapshot.score)}</strong>
          {ranking.previousBest > 0 && <small>PREVIOUS BEST {formatScore(ranking.previousBest)}</small>}
        </div>

        <div className="result-board" aria-label="Local top five stage scores">
          <div className="result-board-title"><span>LOCAL TOP 5</span><span>STAGE {snapshot.stageId + 1}</span></div>
          <ol>
            {[0, 1, 2, 3, 4].map((index) => {
              const score = ranking.topScores[index];
              const isCurrent = ranking.placement === index + 1;
              return (
                <li key={index} className={isCurrent ? 'is-current' : ''}>
                  <b>{String(index + 1).padStart(2, '0')}</b>
                  <span>{score === undefined ? '—' : formatScore(score)}</span>
                  <em>{isCurrent ? 'CURRENT' : ''}</em>
                </li>
              );
            })}
          </ol>
        </div>

        <div className="panel-actions result-actions">
          <button data-menu-item type="button" className="button primary" onClick={() => runtime.advanceStage()}>
            {snapshot.stageId < 3 ? 'ENTER NEXT LAYER' : 'VIEW ENDING'}
          </button>
          <button data-menu-item type="button" className="button" onClick={() => runtime.returnToTitle()}>TITLE</button>
        </div>
      </section>
    );
  }

  return (
    <section className="modal-panel result-panel" aria-labelledby="result-title">
      <p className="eyebrow">ROUTE LAYER STABILIZED</p>
      <h2 id="result-title">STAGE {snapshot.stageId + 1} CLEAR</h2>
      <div className="result-score"><span>BONUS</span><strong>{formatScore(snapshot.stageBonus)}</strong></div>
      <div className="result-score"><span>TOTAL</span><strong>{formatScore(snapshot.score)}</strong></div>
      <p>{snapshot.stageId < 3 ? 'The next record layer is now addressable.' : 'The central archive has resumed its light sequence.'}</p>
      <div className="panel-actions">
        <button data-menu-item type="button" className="button primary" onClick={() => runtime.advanceStage()}>{snapshot.stageId < 3 ? 'ENTER NEXT LAYER' : 'VIEW ENDING'}</button>
        <button data-menu-item type="button" className="button" onClick={() => runtime.returnToTitle()}>TITLE</button>
      </div>
    </section>
  );
});

export const GameOverPanel = memo(function GameOverPanel({ runtime, snapshot }: RuntimeProps) {
  return (
    <section className="modal-panel gameover-panel" aria-labelledby="gameover-title">
      <p className="eyebrow">ROUTE SIGNAL LOST</p>
      <h2 id="gameover-title">ARCHIVE DARK</h2>
      <p>The VEIL SKIMMER can re-enter this layer with recalibrated hull integrity.</p>
      <p className="continue-rule">CONTINUES USED {snapshot.continuesUsed} · LUX RETENTION 50%</p>
      <div className="panel-actions vertical">
        <button data-menu-item type="button" className="button primary" onClick={() => runtime.continueRun()}>CONTINUE</button>
        <button data-menu-item type="button" className="button" onClick={() => runtime.retryStage()}>RETRY STAGE</button>
        <button data-menu-item type="button" className="button danger" onClick={() => runtime.returnToTitle()}>TITLE</button>
      </div>
    </section>
  );
});

export const EndingPanel = memo(function EndingPanel({ runtime, snapshot }: RuntimeProps) {
  return (
    <section className="ending-panel" aria-labelledby="ending-title">
      <div className="ending-light" aria-hidden="true"><i /><i /><i /></div>
      <p className="eyebrow">ALL FOUR ROUTES RE-ILLUMINATED</p>
      <h2 id="ending-title">THE ARCHIVE REMEMBERS</h2>
      <p>Not as a monument, but as a navigable field of light. The VEIL SKIMMER releases its three Nodes into the restored routes, where every signal can become a passage again.</p>
      <div className="ending-stats">
        <span>SCORE {formatScore(snapshot.score)}</span>
        <span>DIFFICULTY {snapshot.difficultyName}</span>
        <span>CONTINUES {snapshot.continuesUsed}</span>
      </div>
      <button data-menu-item type="button" className="button primary" onClick={() => runtime.returnToTitle()}>RETURN TO TITLE</button>
    </section>
  );
});

export const ErrorPanel = memo(function ErrorPanel({ snapshot }: { snapshot: RuntimeUISnapshot }) {
  return (
    <section className="modal-panel error-panel" role="alert" aria-labelledby="error-title">
      <p className="eyebrow">SAFE FALLBACK</p>
      <h2 id="error-title">WEBGL RUNTIME ERROR</h2>
      <p>{snapshot.error || 'The renderer could not initialize.'}</p>
      <p>Use a WebGL2-capable browser with hardware acceleration enabled, then reload the page.</p>
    </section>
  );
});

interface ControlGroupProps { title: string; rows: readonly (readonly [string, string])[]; }
function ControlGroup({ title, rows }: ControlGroupProps) {
  return (
    <section className="control-group">
      <h3>{title}</h3>
      <dl>{rows.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>
    </section>
  );
}
